import type { ReportStrategyResponse } from "../../../types/reports";
import {
  formatBrokerAccountRefLabel,
  resolveEntityBrokerAccounts,
} from "../../../utils/brokerAccountRef";

export type WorkspaceAttachExistingStrategyItem = {
  id: string;
  title: string;
  /** Compact cell copy — one account code, or "N accounts". */
  subtitle?: string;
  /** Full account list for tooltip + search. */
  detail?: string;
};

/** Compact accounts cell: one code, or a count when there are several. */
export function compactAccountListLabel(labels: string[]): {
  subtitle?: string;
  detail?: string;
} {
  const cleaned = labels.map((label) => label.trim()).filter(Boolean);
  if (cleaned.length === 0) return {};
  if (cleaned.length === 1) {
    const only = cleaned[0]!;
    return { subtitle: only, detail: only };
  }
  return {
    subtitle: `${cleaned.length} accounts`,
    detail: cleaned.join(" · "),
  };
}

/** Strategies the user can place on this canvas — including ones already on another account. */
export function filterStrategiesForAttachExisting(
  rows: ReportStrategyResponse[],
  excludeEntityIds: ReadonlySet<string>
): WorkspaceAttachExistingStrategyItem[] {
  return rows
    .filter((row) => {
      const id = String(row.id ?? "").trim();
      if (!id || excludeEntityIds.has(id)) return false;
      return !row.is_archived;
    })
    .map((row) => {
      const accounts = resolveEntityBrokerAccounts({
        accounts: row.accounts,
        brokerName: row.broker_name,
        brokerAccountCode: row.broker_account_code,
      });
      const compact = compactAccountListLabel(
        accounts.map(formatBrokerAccountRefLabel)
      );
      return {
        id: row.id,
        title: row.name?.trim() || "Untitled strategy",
        subtitle: compact.subtitle || row.description?.trim() || undefined,
        detail: compact.detail,
      };
    });
}
